import { useMutation, useQueryClient } from 'react-query';
import { ClientDto } from '../../../../api';
import { queryKeys } from '../../../../constants';
import useAuthenticatedClients from '../../../../hooks/useAuthenticatedClients';
import { toast } from 'react-toastify';
import { onMutationError } from '../../../../utils/Utils';

function ClientQueries() {
  const { CLIENTS_QUERY_KEY } = queryKeys;
  const { getClientsClient } = useAuthenticatedClients();
  const queryClient = useQueryClient();

  /**
   * Updates clients when a new client is created
   */
  const { mutate: mutateCreateClient } = useMutation<ClientDto, Error, ClientDto>(createClient, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(CLIENTS_QUERY_KEY);
      toast.success('Successfully created client');
    },
    onError: onMutationError,
  });

  /**
   * Updates clients when an existing client is updated
   */
  const { mutate: mutateUpdateClient } = useMutation<ClientDto, Error, ClientDto>(updateClient, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(CLIENTS_QUERY_KEY);
      toast.success('Successfully updated client');
    },
    onError: onMutationError,
  });

  /**
   * Updates clients when a client is deleted
   */
  const deleteClientMutation = useMutation<void, Error, number>(deleteClient, {
    onSuccess: async () => {
      queryClient.invalidateQueries(CLIENTS_QUERY_KEY);
      toast.success('Successfully deleted client');
    },
    onError: onMutationError,
  });

  /**
   * Creates a POST request to create a new client
   * @param clientDto Client DTO containing data to create a new client
   */
  async function createClient(clientDto: ClientDto) {
    const client = await getClientsClient();
    const newClient = await client.createClient(clientDto);
    return newClient;
  }

  /**
   * Creates a PUT request to update an existing client
   * @param clientDto Client DTO containing data to update an existing client
   */
  async function updateClient(clientDto: ClientDto) {
    const client = await getClientsClient();
    const updatedClient = await client.updateClient(clientDto);
    return updatedClient;
  }

  /**
   * Creates a DELETE request to delete a client
   * @param id the id of the client to delete
   */
  async function deleteClient(id: number) {
    const client = await getClientsClient();
    await client.deleteClient(id);
  }

  return {
    mutateCreateClient,
    mutateUpdateClient,
    deleteClientMutation,
  };
}

export default ClientQueries;
